import React, { useState } from 'react';
import {
    Box,
    Button,
    Alert,
    Card,
    CardContent,
    CardActions,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    PhotoLibrary as PhotoLibraryIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axios from 'axios';

const LogoUpload = ({
    currentLogo,
    onLogoChange,
    onError,
    onSuccess,
    uploadEndpoint = '/api/partners/logo',
    deleteEndpoint = '/api/partners/logo',
    partnerId = null, // For uploading on behalf of another partner
    compact = false, // For inline use in forms
    showCurrentLogo = true,
    allowDelete = true
}) => {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                onError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
                return;
            }

            // Validate file size (5MB)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                onError('File too large. Maximum size is 5MB.');
                return;
            }

            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            setUploading(true);

            const formData = new FormData();
            formData.append('logo', selectedFile);
            
            // Add partner ID if uploading on behalf of another partner
            if (partnerId) {
                formData.append('partnerId', partnerId);
            }

            const response = await axios.post(uploadEndpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            onSuccess(response.data.message || 'Logo uploaded successfully!');
            onLogoChange(response.data.logoUrl || response.data.cloudFrontUrl);
            setSelectedFile(null);
            
            // Clear file input
            const fileInput = document.getElementById(`logo-upload-input-${partnerId || 'self'}`);
            if (fileInput) fileInput.value = '';

        } catch (error) {
            console.error('Error uploading logo:', error);
            onError(error.response?.data?.error || 'Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteLogo = async () => {
        try {
            setDeleting(true);

            const deleteUrl = partnerId ? `${deleteEndpoint}/${partnerId}` : deleteEndpoint;
            await axios.delete(deleteUrl);

            onSuccess('Logo deleted successfully!');
            onLogoChange(null);
            setDeleteDialogOpen(false);

        } catch (error) {
            console.error('Error deleting logo:', error);
            onError(error.response?.data?.error || 'Failed to delete logo');
            setDeleteDialogOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    const clearSelection = () => {
        setSelectedFile(null);
        const fileInput = document.getElementById(`logo-upload-input-${partnerId || 'self'}`);
        if (fileInput) fileInput.value = '';
    };

    if (compact) {
        // Compact version for inline use in forms
        return (
            <Box>
                {currentLogo && showCurrentLogo && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Current Logo:
                        </Typography>
                        <Box
                            component="img"
                            src={currentLogo}
                            alt="Current logo"
                            sx={{
                                maxWidth: 200,
                                maxHeight: 80,
                                objectFit: 'contain',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                                backgroundColor: 'grey.50'
                            }}
                        />
                        {allowDelete && (
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    color="error"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={deleting}
                                >
                                    Remove
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ mb: 2 }}>
                    <input
                        id={`logo-upload-input-${partnerId || 'self'}`}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor={`logo-upload-input-${partnerId || 'self'}`}>
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                            fullWidth
                        >
                            {currentLogo ? 'Replace Logo' : 'Upload Logo'}
                        </Button>
                    </label>
                </Box>

                {selectedFile && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ py: 1 }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ pt: 0 }}>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleUpload}
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={clearSelection}
                                disabled={uploading}
                            >
                                Cancel
                            </Button>
                        </CardActions>
                    </Card>
                )}

                <Typography variant="caption" color="text.secondary">
                    Supported: JPEG, PNG, GIF, WebP • Max 5MB • Recommended: 300x100px
                </Typography>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => !deleting && setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Remove Logo</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove this logo? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => setDeleteDialogOpen(false)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteLogo}
                            color="error"
                            disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                        >
                            {deleting ? 'Removing...' : 'Remove'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // Full version for dedicated logo management pages
    return (
        <Box>
            {/* Current Logo Display */}
            {showCurrentLogo && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhotoLibraryIcon />
                        Current Logo
                    </Typography>
                    
                    {currentLogo ? (
                        <Card sx={{ maxWidth: 400 }}>
                            <Box
                                component="img"
                                src={currentLogo}
                                alt="Current partner logo"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: 200,
                                    objectFit: 'contain',
                                    backgroundColor: 'grey.50',
                                    p: 2
                                }}
                            />
                            {allowDelete && (
                                <CardActions>
                                    <Button
                                        startIcon={<DeleteIcon />}
                                        color="error"
                                        onClick={() => setDeleteDialogOpen(true)}
                                        disabled={deleting}
                                    >
                                        Delete Logo
                                    </Button>
                                </CardActions>
                            )}
                        </Card>
                    ) : (
                        <Box
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                backgroundColor: 'grey.50',
                                borderRadius: 2,
                                border: '2px dashed',
                                borderColor: 'grey.300'
                            }}
                        >
                            <PhotoLibraryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No logo uploaded yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Upload a logo to personalize the partner portal
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Upload Section */}
            <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon />
                    {currentLogo ? 'Replace Logo' : 'Upload Logo'}
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <input
                        id={`logo-upload-input-${partnerId || 'self'}`}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor={`logo-upload-input-${partnerId || 'self'}`}>
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ py: 2 }}
                        >
                            Choose Image File
                        </Button>
                    </label>
                </Box>

                {selectedFile && (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                Selected: {selectedFile.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                            >
                                {uploading ? 'Uploading...' : 'Upload Logo'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={clearSelection}
                                disabled={uploading}
                            >
                                Cancel
                            </Button>
                        </CardActions>
                    </Card>
                )}

                <Box sx={{ backgroundColor: 'info.main', color: 'info.contrastText', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Logo Requirements:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                        <li>Supported formats: JPEG, PNG, GIF, WebP</li>
                        <li>Maximum file size: 5MB</li>
                        <li>Recommended dimensions: 300x100px or similar ratio</li>
                        <li>For best results, use a transparent background (PNG)</li>
                        <li>Uploading a new logo will replace the current one</li>
                    </Typography>
                </Box>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Logo</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this logo? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteLogo}
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LogoUpload;
